/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import moment from 'moment';

import { useRetracked } from 'js/lib/retracked';
import { useLocation, useNavigate } from 'js/lib/useRouter';
import { get as getUser } from 'js/lib/user';
import { humanizeLearningTime } from 'js/utils/DateTimeUtils';

import { courseSlug as constantsCourseSlug } from 'pages/open-course/common/constants';

import { Typography2 } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';
import type { EventData } from '@coursera/event-pulse/react';

import { NavigationButtonContainer } from 'bundles/ai-coach-platform/components/patterns/chat/recommendations/NavigationButtonContainer';
import { toggleChat } from 'bundles/ai-coach-platform/components/patterns/chat/store/actions';
import type {
  ExtendedAiCoachLectureItemMetadata,
  ExtendedAiCoachReadingItemMetadata,
  NavigationButtonItem,
} from 'bundles/ai-coach-platform/components/patterns/chat/types';
import { generateCourseItemLink } from 'bundles/ai-coach-platform/components/patterns/chat/utils/recommendationsUtils';
import { CoachMode } from 'bundles/ai-coach-platform/constants';
import type { ItemNavigationButtonFragment } from 'bundles/ai-coach-platform/queries/__generated__/navigationButtonFragment';
import { useCoachTrackingContext } from 'bundles/ai-coach-platform/utils/tracking';

type Props = { navigationButton: ItemNavigationButtonFragment };

const styles = {
  itemNavigationButtonContainer: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
    margin-top: var(--cds-spacing-200);
    margin-bottom: var(--cds-spacing-200);
  `,
  leadInText: css`
    color: var(--cds-color-grey-800);
  `,
};

export const NavigationButtons = (props: Props): React.ReactElement | null => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const trackRetracked = useRetracked();
  const track = useTracker();
  const trackingContext = useCoachTrackingContext()?.data;
  const location = useLocation().pathname;
  const currentUser = getUser();

  const { navigationButton } = props;
  const navigationButtonType = navigationButton?.__typename;

  // Initialize tracking data early with safe defaults
  const trackingData: EventData<'view_coach_recommended_navigation'> = {
    coachNavigationMessage: navigationButton?.navigationLeadingMessage ?? '',
    coachNavigationButtonType: navigationButtonType ?? 'UNSUPPORTED_NAV_BUTTON_TYPE',
    coachNavigationTargetUrl: '', // Will be updated if valid
    coach: {
      mode: trackingContext?.mode ?? CoachMode.Basic,
    },
  };
  // COACH-2390: mirrors `trackingData` for the new consolidated view_coach_element event, dual-emitted
  // alongside the legacy view_coach_recommended_navigation event during the observation period. Kept as a
  // separate mutable object (rather than spread at track time) so the `coachNavigationTargetUrl` update below
  // is reflected once the visibility tracker actually fires.
  const elementTrackingData: EventData<'view_coach_element'> = {
    ...trackingData,
    viewElementType: 'recommended_navigation',
  };

  // Call hook unconditionally at the top level
  const linkVisibilityRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_coach_recommended_navigation',
    trackingData
  );

  const linkElementVisibilityRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_coach_element',
    elementTrackingData
  );
  const linkRef = useCallback<(node: HTMLDivElement | null) => void>(
    (node) => {
      linkVisibilityRef.current = node;
      linkElementVisibilityRef.current = node;
    },
    [linkVisibilityRef, linkElementVisibilityRef]
  );

  // Currently, we only support item navigation buttons. But eventually we may support redirect link navigation buttons as well.
  if (navigationButtonType !== 'AiCoach_ItemNavigationButton' || !navigationButton?.itemMetadata) {
    return null;
  }

  // we only support navigation buttons for reading and lectures currently
  const itemMetadata = navigationButton.itemMetadata as
    ExtendedAiCoachReadingItemMetadata | ExtendedAiCoachLectureItemMetadata;

  const buttonLabel = navigationButton?.buttonLabel;
  // `itemMetadata.course.slug` will be missing for text streaming since subscriptions
  // doesn't support federated entities. We can use the constants.courseSlug as the fallback in this case
  // because currently the navigation buttons only navigate to the same course.
  // TODO: use `Course.queryById` to fetch the actual slug and then render navigation buttons
  const courseSlug = itemMetadata.course?.slug ?? constantsCourseSlug;
  const itemSlug = itemMetadata.itemSlug;
  const itemId = itemMetadata.itemId;
  const title = itemMetadata.itemTitle;
  let itemType: NavigationButtonItem;
  let itemDuration = [''];

  switch (itemMetadata.__typename) {
    case 'AiCoach_ReadingItemMetadata':
      itemType = 'supplement';
      itemDuration = [humanizeLearningTime(moment.duration(itemMetadata.readingDuration)) || ''];
      break;
    case 'AiCoach_LectureItemMetadata':
      itemType = 'lecture';
      itemDuration = [humanizeLearningTime(moment.duration(itemMetadata.lectureDuration))];
      break;
    default:
      itemType = null;
  }

  if (!itemType) {
    return null;
  }

  const courseItemLink = generateCourseItemLink({ itemType, courseSlug, itemSlug, itemId });

  // Update tracking data URL now that we have valid data
  trackingData.coachNavigationTargetUrl = courseItemLink;
  elementTrackingData.coachNavigationTargetUrl = courseItemLink;

  const handleNavButtonClick = () => {
    // V2 eventing
    trackRetracked({
      trackingData: {
        userId: currentUser.id,
        userLocale: currentUser.locale,
        timestamp: Date.now(),
        navMessage: navigationButton.navigationLeadingMessage,
        navButtonType: navigationButtonType,
        navTargetUrl: courseItemLink,
        referrerUrl: location,
      },
      trackingName: 'ai_course_coach_recommended_navigation',
      action: `click`,
    });

    // V3 eventing
    track('click_coach_recommended_navigation', trackingData);

    // Coach eventing consolidation [COACH-2404]: dual-emit onto the new ClickCoachElement schema
    track('click_coach_element', {
      clickElementType: 'recommended_navigation',
      ...trackingData,
    });

    dispatch(toggleChat());
    navigate(courseItemLink, { replace: false });
  };

  return (
    <div css={styles.itemNavigationButtonContainer} ref={linkRef}>
      <Typography2 variant="bodyPrimary" component="p" css={styles.leadInText}>
        <strong>{navigationButton.navigationLeadingMessage}</strong>
      </Typography2>

      <NavigationButtonContainer
        itemId={itemId}
        title={title}
        itemType={itemType}
        itemDuration={itemDuration}
        buttonLabel={buttonLabel}
        handleNavButtonClick={handleNavButtonClick}
      />
    </div>
  );
};

export default NavigationButtons;
